/**
 * File Utilities
 * 
 * Helpers for file operations including import/export.
 */

import type { ISavedPipeline } from '@/interfaces/IStorage';

import { FILE_EXTENSIONS } from './constants';
import { downloadFile, readFileAsArrayBuffer, readFileAsText } from './utils';

/**
 * Export a pipeline to a file.
 */
export function exportPipeline(pipeline: ISavedPipeline): void {
  const content = JSON.stringify(pipeline, null, 2);
  const filename = `${pipeline.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}${FILE_EXTENSIONS.PIPELINE}`;
  downloadFile(content, filename, 'application/json');
}

/**
 * Export multiple pipelines to a single file.
 */
export function exportAllPipelines(pipelines: ISavedPipeline[]): void {
  const content = JSON.stringify({
    version: '1.0.0',
    exportedAt: new Date().toISOString(),
    pipelines,
  }, null, 2);
  downloadFile(content, `prism-pipelines${FILE_EXTENSIONS.JSON}`, 'application/json');
}

/**
 * Import a pipeline from a file.
 */
export async function importPipeline(file: File): Promise<ISavedPipeline | null> {
  try {
    const content = await readFileAsText(file);
    const data = JSON.parse(content);
    
    // Validate pipeline structure
    if (!validatePipeline(data)) {
      console.error('Invalid pipeline format');
      return null;
    }
    
    return data as ISavedPipeline;
  } catch (error) {
    console.error('Failed to import pipeline:', error);
    return null;
  }
}

/**
 * Import multiple pipelines from a file.
 */
export async function importPipelines(file: File): Promise<ISavedPipeline[]> {
  try {
    const content = await readFileAsText(file);
    const data = JSON.parse(content);
    
    // Check if it's a bulk export
    if (data.pipelines && Array.isArray(data.pipelines)) {
      return data.pipelines.filter(validatePipeline);
    }
    
    // Single pipeline
    if (validatePipeline(data)) {
      return [data];
    }
    
    return [];
  } catch (error) {
    console.error('Failed to import pipelines:', error);
    return [];
  }
}

/**
 * Validate pipeline structure.
 */
function validatePipeline(data: unknown): data is ISavedPipeline {
  if (!data || typeof data !== 'object') return false;
  
  const pipeline = data as Record<string, unknown>;
  
  return (
    typeof pipeline.id === 'string' &&
    typeof pipeline.name === 'string' &&
    Array.isArray(pipeline.steps)
  );
}

/**
 * Export output as a text file.
 */
export function exportOutput(content: string, filename = 'output.txt'): void {
  downloadFile(content, filename, 'text/plain');
}

/**
 * Create a file input trigger for importing.
 */
export function createFileInput(
  accept: string,
  onSelect: (file: File) => void,
  options?: {
    multiple?: boolean;
    onSelectFiles?: (files: File[]) => void;
  }
): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  if (options?.multiple) {
    input.multiple = true;
  }
  input.onchange = (e) => {
    const list = (e.target as HTMLInputElement).files;
    const files = list ? Array.from(list) : [];
    if (files.length === 0) return;
    if (options?.multiple && options.onSelectFiles) {
      options.onSelectFiles(files);
      return;
    }
    onSelect(files[0]!);
  };
  input.click();
}

/**
 * Trigger import dialog for pipelines.
 */
export function triggerPipelineImport(onImport: (pipeline: ISavedPipeline) => void): void {
  createFileInput('.prism,.swedevtools,.json', async (file) => {
    const pipeline = await importPipeline(file);
    if (pipeline) {
      onImport(pipeline);
    }
  });
}

export type ImportedInputEncoding = 'text' | 'base64';

export interface ImportedInputFile {
  content: string;
  fileName: string;
  mimeType: string;
  encoding: ImportedInputEncoding;
}

function getFileExtension(fileName: string): string {
  const idx = fileName.lastIndexOf('.');
  if (idx === -1) return '';
  return fileName.slice(idx + 1).toLowerCase();
}

function guessMimeTypeFromExtension(ext: string): string | null {
  switch (ext) {
    case 'json':
      return 'application/json';
    case 'csv':
      return 'text/csv';
    case 'tsv':
      return 'text/tab-separated-values';
    case 'txt':
      return 'text/plain';
    case 'md':
    case 'markdown':
      return 'text/markdown';
    case 'xml':
      return 'application/xml';
    case 'yaml':
    case 'yml':
      return 'application/x-yaml';
    case 'toml':
      return 'application/toml';
    case 'html':
    case 'htm':
      return 'text/html';
    case 'svg':
      return 'image/svg+xml';
    default:
      return null;
  }
}

function normalizeMimeType(mimeType: string): string {
  return mimeType.split(';')[0]?.trim().toLowerCase() ?? '';
}

function isTextMimeType(mimeType: string): boolean {
  const normalized = normalizeMimeType(mimeType);
  if (normalized.startsWith('text/')) return true;
  return (
    normalized === 'application/json' ||
    normalized === 'application/xml' ||
    normalized === 'application/x-yaml' ||
    normalized === 'application/toml' ||
    normalized === 'text/xml' ||
    normalized === 'image/svg+xml'
  );
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

/**
 * Import an arbitrary input file for processing.
 * - Text files are read as UTF-8 string.
 * - Binary files are converted to a Base64 string.
 *
 * The returned `mimeType` is the file's original MIME type (best effort).
 */
export async function importInputFile(file: File): Promise<ImportedInputFile> {
  const ext = getFileExtension(file.name);
  const mimeType = normalizeMimeType(file.type || guessMimeTypeFromExtension(ext) || 'application/octet-stream');

  if (isTextMimeType(mimeType)) {
    const content = await readFileAsText(file);
    return { content, fileName: file.name, mimeType, encoding: 'text' };
  }

  const buffer = await readFileAsArrayBuffer(file);
  const content = arrayBufferToBase64(buffer);
  return { content, fileName: file.name, mimeType, encoding: 'base64' };
}

